<template>
  <div>
    <div
      class="video-col-left"
    >
      <transition name="fade">
        <div
          class="video-item"
          v-if="localTracks.length > 0"
        >
          <LocalVideoComponent
            ref="localVideoComponentInstance"
            :gameState="gameState"
            :isHost="isHost"
            :isGameInWaitingState="isGameInWaitingState"
            :tracks="localTracks"
            :isLocalAudioMuted="isLocalAudioMuted"
            :isLocalVideoMuted="isLocalVideoMuted"
            :localPlayerHasAnswered="localPlayerHasAnswered"
            :localPlayerHasVoted="localPlayerHasVoted"
            :primaryColor="primaryColor"
            :secondaryColor="secondaryColor"
            :backgroundColor="backgroundColor"
            @play-sound-effect="propagateEvent('play-sound-effect', $event)"
          />
        </div>
      </transition>
      <div
        class="video-item"
        v-for="remoteParticipant in leftSideRemoteParticipants"
        :key="remoteParticipant.sid"
      >
        <RemoteVideoComponent
          :gameState="gameState"
          :participant="remoteParticipant"
          :isGameInWaitingState="isGameInWaitingState"
          @play-sound-effect="propagateEvent('play-sound-effect', $event)"
        />
      </div>
    </div>
    <div
      class="video-col-right"
    >
      <div
        class="video-item"
        v-for="remoteParticipant in rightSideRemoteParticipants"
        :key="remoteParticipant.sid"
      >
        <RemoteVideoComponent
          :gameState="gameState"
          :participant="remoteParticipant"
          :isGameInWaitingState="isGameInWaitingState"
          @play-sound-effect="propagateEvent('play-sound-effect', $event)"
        />
      </div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import LocalVideoComponent from '@/components/gameComponents/LocalVideoComponent';
import RemoteVideoComponent from '@/components/gameComponents/RemoteVideoComponent';

const { connect } = require('twilio-video');

export default Vue.extend({
  name: 'VideoView',
  components: {
    LocalVideoComponent,
    RemoteVideoComponent
  },
  props: {
    localTracks: Array,
    videoRoomcode: String,
    gameState: Object,
    isHost: Boolean,
    isLocalAudioMuted: Boolean,
    isLocalVideoMuted: Boolean,
    primaryColor: String,
    secondaryColor: String,
    backgroundColor: String
  },
  data: () => ({
    remoteParticipants: [],
    profileImages: [],
    twilioRoom: {},
    videoToken: '',    // jwt string
    localPlayerHasAnswered: false,
    localPlayerHasVoted: false
  }),
  watch: {
    remoteParticipants: function() {
      this.$nextTick(function() {
        window.dispatchEvent(new Event('resize'));
      });
    }
  },
  computed: {
    auth0UserId: function () {
      return this.$auth.user.sub.replace(/\|/g, '_');
    },
    idToken: function () {
      return this.$auth.idToken;
    },
    leftSideRemoteParticipants: function () {
      const leftParticipants = [];
      for (let i = 1; i < this.remoteParticipants.length; i += 2) {
        leftParticipants.push(this.remoteParticipants[i]);
      }
      return leftParticipants;
    },
    rightSideRemoteParticipants: function () {
      const rightParticipants = [];
      for (let i = 0; i < this.remoteParticipants.length; i += 2) {
        rightParticipants.push(this.remoteParticipants[i]);
      }
      return rightParticipants;
    },
    isGameInWaitingState: function () {
      return 'gameStatus' in this.gameState &&
             (this.gameState.gameStatus === 'waitingForPlayers' ||
             this.gameState.gameStatus === 'waitingToStart');
    },
  },
  methods: {
    connectToTwilioRoom () {
      // Get Twilio API access token from backend
      this.axios.post(`${ process.env.VUE_APP_BACKEND_API_URL }/videotoken`, {
        videoRoomcode: this.videoRoomcode,
        userId: this.auth0UserId
      }, {
        headers: {
          'Content-Type': 'application/json', 
          'Authorization': `Bearer ${this.idToken}`
        }
      }).then(res => {
        if (res.status === 200) {
          this.videoToken = res.data.videoToken;
        }

        // Connect to room using token, setup listeners
        connect(this.videoToken, {
          name: this.videoRoomcode,
          tracks: this.localTracks,
          bandwidthProfile: {
            video: {
              mode: 'grid',
              maxTracks: 10,
              renderDimensions: {
                high: {height: 720, width: 720},
                standard: {height: 480, width: 480},
                low: {height: 144, width: 144}
              }
            }
          },
          maxAudioBitrate: 16000,
          preferredVideoCodecs: [{ codec: 'VP8', simulcast: true }],
          networkQuality: {local: 1, remote: 1}
        }).then(room => {
          this.twilioRoom = room;

          room.participants.forEach(participant => {
            participant.hasAnswered = false;
            participant.hasVoted = false;
            participant.joinedIndex = this.remoteParticipants.length + 2;
            this.remoteParticipants.push(participant);
          });

          room.on('participantConnected', participant => {
            participant.hasAnswered = false;
            participant.hasVoted = false;
            participant.joinedIndex = this.remoteParticipants.length + 2;
            this.remoteParticipants.push(participant);
          });

          room.on('participantDisconnected', participant => {
            // iterate in reverse because we are removing elements from the array in-place
            for (let index = this.remoteParticipants.length - 1; index >= 0; --index) {
              if (this.remoteParticipants[index].sid === participant.sid) {
                this.remoteParticipants.splice(index, 1);
              }
            }

            this.remoteParticipants.forEach((participant, index) => {
              participant.joinedIndex = index + 2;
            });
          });

          room.on('disconnected', room => {
            room.localParticipant.tracks.forEach(publication => {
              publication.track.stop();
              const attachedElements = publication.track.detach();
              attachedElements.forEach(element => element.remove());
            });
          });
        }).catch(error => {
          console.log('Error:', error);
        });
      }).catch(error => {
        console.log('Error:', error);
      });
    },
    disconnectFromTwilioRoom () {
      this.twilioRoom.disconnect();
    },
    setPlayerHasAnswered (identity) {
      this.remoteParticipants.forEach(participant => {
        if (participant.identity === identity) {
          participant.hasAnswered = true;
        }
      });
    },
    setPlayerHasVoted (identity) {
      this.remoteParticipants.forEach(participant => {
        if (participant.identity === identity) {
          participant.hasVoted = true;
        }
      });
    },
    resetPlayersHaveAnswered () {
      this.localPlayerHasAnswered = false;

      this.remoteParticipants.forEach(participant => {
        participant.hasAnswered = false;
      });
    },
    resetPlayersHaveVoted () {
      this.localPlayerHasVoted = false;

      this.remoteParticipants.forEach(participant => {
        participant.hasVoted = false;
      });
    },
    propagateEvent(eventName, eventData) {
      this.$emit(eventName, eventData);
    }
  },
  mounted: function () {
    this.$nextTick(function() {
      if (process.env.VUE_APP_ENABLE_VIDEO === 'true') {
        this.connectToTwilioRoom();
      } else {
        console.log('Skipping twilio room connection');
      }
    });
  },
  beforeDestroy: function () {
    if (process.env.VUE_APP_ENABLE_VIDEO === 'true') {
      this.disconnectFromTwilioRoom();
      this.remoteParticipants = [];  // destroy all remote video components
    }
  }
})
</script>

<style lang="scss">
@import '@/styles/video-view-styles.scss';
</style>
